import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { EventCard, MarketCard, type EventGroupCard, type Market } from "../components/MarketCard";

type Cat = { id: string; slug: string; name: string; icon: string; color: string; description: string; market_count: number };

const SORTS: { id: string; label: string }[] = [
  { id: "trending", label: "Trending" },
  { id: "new", label: "Newest" },
  { id: "closing", label: "Closing" },
  { id: "volume", label: "Volume" },
  { id: "movers", label: "Movers" },
];

export function MarketsPage() {
  const [params, setParams] = useSearchParams();
  const sort = params.get("sort") ?? "trending";
  const category = params.get("category") ?? "";
  const q = params.get("q") ?? "";
  const [minP, setMinP] = useState(1);
  const cats = useQuery({ queryKey: ["cats"], queryFn: () => api<{ categories: Cat[] }>("/markets/categories") });
  const groups = useQuery({ queryKey: ["groups"], queryFn: () => api<{ groups: EventGroupCard[] }>("/markets/groups") });
  const markets = useQuery({
    queryKey: ["markets", sort, category, q],
    queryFn: () => api<{ markets: Market[] }>(`/markets?status=OPEN&sort=${sort}&category=${category}&q=${encodeURIComponent(q)}`),
  });
  const filtered = useMemo(
    () => (markets.data?.markets ?? []).filter((m) => m.yes_price_cents >= minP),
    [markets.data, minP],
  );
  const selectedCat = cats.data?.categories.find((c) => c.slug === category);

  function setSort(id: string) {
    params.set("sort", id);
    setParams(params);
  }
  function setCategory(slug: string) {
    if (!slug) params.delete("category");
    else params.set("category", slug);
    setParams(params);
  }

  return (
    <div className="discover">
      <section className="discover-hero">
        <div className="wrap discover-hero-inner">
          <div>
            <span className="kicker">Marketplace</span>
            <h1>Discover markets</h1>
            <p>Trade Yes or No on public events. Prices come from the live book.</p>
          </div>
          <label className="discover-search">
            <Search size={18} />
            <input
              value={q}
              onChange={(e) => {
                const v = e.target.value;
                if (v) params.set("q", v);
                else params.delete("q");
                setParams(params);
              }}
              placeholder="Search questions, tags, or categories"
              aria-label="Search markets"
            />
          </label>
        </div>
      </section>

      <div className="wrap">
        <div className="cat-grid" id="categories">
          <button type="button" className={`cat-tile${!category ? " on" : ""}`} onClick={() => setCategory("")}>
            <span className="cat-icon cat-icon-all">All</span>
            <strong>All markets</strong>
            <span>{cats.data?.categories.reduce((n, c) => n + Number(c.market_count || 0), 0) ?? "—"} live</span>
          </button>
          {(cats.data?.categories ?? []).map((c) => (
            <button
              type="button"
              key={c.slug}
              className={`cat-tile${category === c.slug ? " on" : ""}`}
              onClick={() => setCategory(c.slug)}
            >
              <span className="cat-icon" style={{ background: `${c.color}22`, color: c.color }}>{c.icon}</span>
              <strong>{c.name}</strong>
              <span>{c.market_count} live</span>
            </button>
          ))}
        </div>

        {!category && (groups.data?.groups ?? []).length > 0 && (
          <div className="event-row" style={{ marginBottom: 8 }}>
            {groups.data!.groups.slice(0, 8).map((g) => <EventCard key={g.slug} group={g} />)}
          </div>
        )}

        <div className="discover-toolbar">
          <div className="seg" role="tablist" aria-label="Sort markets">
            {SORTS.map((s) => (
              <button key={s.id} type="button" className={sort === s.id ? "on" : ""} onClick={() => setSort(s.id)}>
                {s.label}
              </button>
            ))}
          </div>
          <label className="prob-filter">
            <span>YES at least <strong>{minP}%</strong></span>
            <input type="range" min={1} max={99} value={minP} onChange={(e) => setMinP(Number(e.target.value))} />
          </label>
        </div>

        <div className="discover-meta">
          <h2>{selectedCat ? selectedCat.name : "All markets"}</h2>
          <p>
            {filtered.length} {filtered.length === 1 ? "market" : "markets"}
            {q ? ` matching “${q}”` : ""}
          </p>
        </div>

        {markets.isLoading && <div className="grid-3">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="card" style={{ height: 200 }}><div className="skeleton" /></div>)}</div>}
        {!markets.isLoading && !filtered.length && (
          <div className="empty card"><h3>No markets match those filters.</h3><p>Clear search or pick another category.</p></div>
        )}
        <div className="grid-3">
          {filtered.map((m) => <MarketCard key={m.id} market={m} />)}
        </div>

        <div className="suggest-strip">
          <div>
            <h3>Don’t see a question?</h3>
            <p>Suggest a market. New questions are reviewed before they go live.</p>
          </div>
          <Link to="/suggest" className="btn btn-primary">Suggest a market</Link>
        </div>
      </div>
    </div>
  );
}
